import React, { useState } from "react";
import "./ActivitiesModule.css";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaHourglassHalf,
  FaPlus,
  FaSearch,
  FaFilter,
  FaUsers,
  FaClipboardList,
  FaArrowLeft,
} from "react-icons/fa";

const ActivitiesModule = ({ token, onBack }) => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Sample Activities Data
  const [activities] = useState([
    {
      id: "ACT-101",
      title: "Annual Sports Day & Fun Games",
      category: "Sports",
      targetGrade: "All Classes",
      assignedStaff: "Priya Sundaram",
      scheduledDate: "2026-11-15",
      time: "09:30 AM",
      status: "Upcoming",
      participantsCount: 120,
      notes: "Ground booking completed. Equipment list finalized.",
    },
    {
      id: "ACT-102",
      title: "Finger Painting & Color Exploration",
      category: "Arts & Crafts",
      targetGrade: "Playgroup & Nursery",
      assignedStaff: "Anita Roy",
      scheduledDate: "2026-04-10",
      time: "10:30 AM",
      status: "Completed",
      participantsCount: 45,
      notes: "High engagement. Photos shared with parents via broadcast.",
    },
    {
      id: "ACT-103",
      title: "Earth Day Plant Planting Workshop",
      category: "Environmental",
      targetGrade: "LKG & UKG",
      assignedStaff: "Priya Sundaram",
      scheduledDate: "2026-04-22",
      time: "11:00 AM",
      status: "Completed",
      participantsCount: 52,
      notes: "Pots and saplings distributed. Report generated.",
    },
    {
      id: "ACT-104",
      title: "Phonics Storytelling & Puppet Show",
      category: "Educational",
      targetGrade: "Nursery",
      assignedStaff: "Anita Roy",
      scheduledDate: "2026-05-05",
      time: "10:00 AM",
      status: "Pending",
      participantsCount: 30,
      notes: "Awaiting puppet kit delivery from supplier.",
    },
    {
      id: "ACT-105",
      title: "Grandparents Day Cultural Celebration",
      category: "Cultural",
      targetGrade: "All Classes",
      assignedStaff: "Vikram Malhotra",
      scheduledDate: "2026-09-12",
      time: "02:00 PM",
      status: "Upcoming",
      participantsCount: 110,
      notes: "Hall decorations and invitation card drafting in progress.",
    },
  ]);

  // Calculations for Stats
  const totalCount = activities.length;
  const completedCount = activities.filter((a) => a.status === "Completed").length;
  const upcomingCount = activities.filter((a) => a.status === "Upcoming").length;
  const pendingCount = activities.filter((a) => a.status === "Pending").length;

  // Filter Logic
  const filteredActivities = activities.filter((activity) => {
    const matchesTab =
      activeTab === "all" || activity.status.toLowerCase() === activeTab;
    const matchesCategory =
      selectedCategory === "All" || activity.category === selectedCategory;
    const matchesSearch =
      activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.assignedStaff.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.targetGrade.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesTab && matchesCategory && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <FaCheckCircle className="status-icon completed" />;
      case "Pending":
        return <FaHourglassHalf className="status-icon pending" />;
      case "Upcoming":
        return <FaClock className="status-icon upcoming" />;
      default:
        return null;
    }
  };

  return (
    <div className="activities-container">
      {/* Header */}
      <div className="activities-header">
        <div className="header-title-block">
          {onBack && (
            <button className="back-btn" onClick={onBack} type="button">
              <FaArrowLeft /> Back to Dashboard
            </button>
          )}
          <h2>
            <FaClipboardList className="header-icon" /> Activity Schedule & Staff Management
          </h2>
          <p className="subtext">
            Track yearly events, staff activity reports, and execution status.
          </p>
        </div>
        <button className="btn-primary">
          <FaPlus /> Schedule New Activity
        </button>
      </div>

      {/* Overview Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon-wrapper blue">
            <FaCalendarAlt />
          </div>
          <div className="stat-info">
            <span className="stat-title">Total Planned</span>
            <h3 className="stat-value">{totalCount}</h3>
            <span className="stat-subtext">Yearly Academic Plan</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper green">
            <FaCheckCircle />
          </div>
          <div className="stat-info">
            <span className="stat-title">Completed</span>
            <h3 className="stat-value">{completedCount}</h3>
            <span className="stat-subtext">Reports Conducted</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper orange">
            <FaClock />
          </div>
          <div className="stat-info">
            <span className="stat-title">Upcoming</span>
            <h3 className="stat-value">{upcomingCount}</h3>
            <span className="stat-subtext">Next Scheduled Events</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper purple">
            <FaHourglassHalf />
          </div>
          <div className="stat-info">
            <span className="stat-title">Pending Review</span>
            <h3 className="stat-value">{pendingCount}</h3>
            <span className="stat-subtext">Needs Action / Setup</span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs & Controls */}
      <div className="activities-controls-card">
        <div className="status-tabs">
          <button
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All Activities ({totalCount})
          </button>
          <button
            className={`tab-btn ${activeTab === "upcoming" ? "active" : ""}`}
            onClick={() => setActiveTab("upcoming")}
          >
            Upcoming ({upcomingCount})
          </button>
          <button
            className={`tab-btn ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Conducted / Done ({completedCount})
          </button>
          <button
            className={`tab-btn ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending Setup ({pendingCount})
          </button>
        </div>

        <div className="filters-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by activity, staff, or grade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-box">
            <FaFilter className="filter-icon" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="All">All Categories</option>
              <option value="Arts & Crafts">Arts & Crafts</option>
              <option value="Sports">Sports</option>
              <option value="Educational">Educational</option>
              <option value="Cultural">Cultural</option>
              <option value="Environmental">Environmental</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activities Grid */}
      {filteredActivities.length === 0 ? (
        <div className="no-records-card">
          <p>No activity records found matching the selected filters.</p>
        </div>
      ) : (
        <div className="activity-cards-grid">
          {filteredActivities.map((activity) => (
            <div key={activity.id} className="activity-card">
              <div className="card-header">
                <span className="category-badge">{activity.category}</span>
                <span className={`status-badge ${activity.status.toLowerCase()}`}>
                  {getStatusIcon(activity.status)}
                  {activity.status}
                </span>
              </div>

              <h3 className="activity-title">{activity.title}</h3>

              <div className="activity-meta">
                <div className="meta-item">
                  <FaCalendarAlt className="meta-icon" />
                  <span>
                    {activity.scheduledDate} at {activity.time}
                  </span>
                </div>
                <div className="meta-item">
                  <FaUsers className="meta-icon" />
                  <span>Target: <strong>{activity.targetGrade}</strong></span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">In-Charge Staff:</span>
                  <strong className="staff-name">{activity.assignedStaff}</strong>
                </div>
              </div>

              <div className="activity-footer">
                <div className="participants-pill">
                  {activity.participantsCount} Children Enrolled
                </div>
                <p className="activity-notes">{activity.notes}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivitiesModule;